import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
  FIELD_VALIDATION,
  WORK_EXPERIENCE_VALIDATION,
  EDUCATION_VALIDATION,
  SKILLS_LANGUAGES_VALIDATION,
  PERSONAL_DATA_VALIDATION,
  PROJECT_VALIDATION,
  ALLOWED_VALUES,
  exceedsMaxLength,
  isRequiredFieldValid,
} from '../constants/validation-constants';
import {
  WorkExperienceExtended,
  HighSchoolEducationExtended,
  UniversityEducationExtended,
  LanguageExtended,
  SkillExtended
} from '../models/dashboard/user-from-be';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  /**
   * Get validators for work experience form fields
   */
  getWorkExperienceValidators() {
    return {
      employer: [Validators.required, Validators.maxLength(WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH)],
      position: [Validators.required, Validators.maxLength(WORK_EXPERIENCE_VALIDATION.POSITION_MAX_LENGTH)],
      city: [Validators.maxLength(WORK_EXPERIENCE_VALIDATION.CITY_MAX_LENGTH)],
      country: [Validators.maxLength(WORK_EXPERIENCE_VALIDATION.COUNTRY_MAX_LENGTH)],
      description: [Validators.maxLength(WORK_EXPERIENCE_VALIDATION.DESCRIPTION_MAX_LENGTH)],
      startDate: [Validators.required],
      endDate: [], // Conditional - required only if not currently working
    };
  }

  /**
   * Get validators for high school education form fields
   */
  getHighSchoolValidators() {
    return {
      highSchoolName: [Validators.required, Validators.maxLength(EDUCATION_VALIDATION.HIGH_SCHOOL_NAME_MAX_LENGTH)],
      fieldOfStudy: [Validators.required, Validators.maxLength(EDUCATION_VALIDATION.HIGH_SCHOOL_FIELD_OF_STUDY_MAX_LENGTH)],
      city: [Validators.maxLength(EDUCATION_VALIDATION.HIGH_SCHOOL_CITY_MAX_LENGTH)],
      country: [Validators.maxLength(EDUCATION_VALIDATION.HIGH_SCHOOL_COUNTRY_MAX_LENGTH)],
      startDate: [Validators.required],
      endDate: [Validators.required],
    };
  }

  /**
   * Get validators for university education form fields
   */
  getUniversityValidators() {
    return {
      universityName: [Validators.required, Validators.maxLength(EDUCATION_VALIDATION.UNIVERSITY_NAME_MAX_LENGTH)],
      fieldOfStudy: [Validators.required, Validators.maxLength(EDUCATION_VALIDATION.UNIVERSITY_FIELD_OF_STUDY_MAX_LENGTH)],
      city: [Validators.maxLength(EDUCATION_VALIDATION.UNIVERSITY_CITY_MAX_LENGTH)],
      country: [Validators.maxLength(EDUCATION_VALIDATION.UNIVERSITY_COUNTRY_MAX_LENGTH)],
      startDate: [Validators.required],
      endDate: [], // Conditional - required only if not currently studying
    };
  }

  /**
   * Get validators for project form fields
   */
  getProjectValidators() {
    return {
      title: [Validators.required, Validators.maxLength(PROJECT_VALIDATION.TITLE_MAX_LENGTH)],
      position: [Validators.required, Validators.maxLength(PROJECT_VALIDATION.POSITION_MAX_LENGTH)],
      duration: [Validators.required, Validators.min(PROJECT_VALIDATION.MIN_DURATION)],
      country: [Validators.maxLength(PROJECT_VALIDATION.COUNTRY_MAX_LENGTH)],
    };
  }

  /**
   * Get validators for language form fields
   */
  getLanguageValidators() {
    return {
      language: [Validators.required, Validators.maxLength(SKILLS_LANGUAGES_VALIDATION.LANGUAGE_NAME_MAX_LENGTH)],
      level: [Validators.required, this.languageLevelValidator()],
    };
  }

  /**
   * Get validators for skill form fields
   */
  getSkillValidators() {
    return {
      skillName: [Validators.required, Validators.maxLength(SKILLS_LANGUAGES_VALIDATION.SKILL_NAME_MAX_LENGTH)],
      level: [Validators.required, this.skillLevelValidator()],
    };
  }

  /**
   * Custom validator for language levels
   */
  private languageLevelValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const isValid = ALLOWED_VALUES.LANGUAGE_LEVELS.includes(control.value as any);
      return isValid ? null : { invalidLanguageLevel: true };
    };
  }

  /**
   * Custom validator for skill levels
   */
  private skillLevelValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const isValid = ALLOWED_VALUES.SKILL_LEVELS.includes(control.value as any);
      return isValid ? null : { invalidSkillLevel: true };
    };
  }

  /**
   * Validate work experience data for autosave
   * Returns true if the data is valid and can be saved
   */
  isWorkExperienceValid(work: WorkExperienceExtended): boolean {
    // Required fields
    if (!isRequiredFieldValid(work.employer)) return false;
    if (!isRequiredFieldValid(work.position)) return false;
    if (!work.startDate) return false;
    if (!work.endDate && !work.current) return false;
    if (!work.current && this.isEndBeforeStart(work.startDate, work.endDate)) return false;

    // Max length validations
    if (exceedsMaxLength(work.employer, WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH)) return false;
    if (exceedsMaxLength(work.position, WORK_EXPERIENCE_VALIDATION.POSITION_MAX_LENGTH)) return false;
    if (exceedsMaxLength(work.city, WORK_EXPERIENCE_VALIDATION.CITY_MAX_LENGTH)) return false;
    if (exceedsMaxLength(work.country, WORK_EXPERIENCE_VALIDATION.COUNTRY_MAX_LENGTH)) return false;
    if (exceedsMaxLength(work.description, WORK_EXPERIENCE_VALIDATION.DESCRIPTION_MAX_LENGTH)) return false;

    return true;
  }

  /**
   * Get validation error message for work experience
   */
  getWorkExperienceValidationMessage(work: WorkExperienceExtended): string {
    const errors: string[] = [];

    // Check required fields
    if (!isRequiredFieldValid(work.employer)) {
      errors.push('Employer is required');
    }
    if (!isRequiredFieldValid(work.position)) {
      errors.push('Position is required');
    }
    if (!work.startDate) {
      errors.push('Start date is required');
    }
    if (!work.endDate && !work.current) {
      errors.push('End date is required');
    }
    if (!work.current && this.isEndBeforeStart(work.startDate, work.endDate)) {
      errors.push('End date must be after start date');
    }

    // Check max lengths
    if (exceedsMaxLength(work.employer, WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH)) {
      errors.push(`Employer exceeds ${WORK_EXPERIENCE_VALIDATION.EMPLOYER_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(work.position, WORK_EXPERIENCE_VALIDATION.POSITION_MAX_LENGTH)) {
      errors.push(`Position exceeds ${WORK_EXPERIENCE_VALIDATION.POSITION_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(work.city, WORK_EXPERIENCE_VALIDATION.CITY_MAX_LENGTH)) {
      errors.push(`City exceeds ${WORK_EXPERIENCE_VALIDATION.CITY_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(work.country, WORK_EXPERIENCE_VALIDATION.COUNTRY_MAX_LENGTH)) {
      errors.push(`Country exceeds ${WORK_EXPERIENCE_VALIDATION.COUNTRY_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(work.description, WORK_EXPERIENCE_VALIDATION.DESCRIPTION_MAX_LENGTH)) {
      errors.push(`Description exceeds ${WORK_EXPERIENCE_VALIDATION.DESCRIPTION_MAX_LENGTH} characters`);
    }

    return errors.join(', ');
  }

  /**
   * Validate project data
   */
  isProjectValid(project: any): boolean {
    // Required fields
    if (!isRequiredFieldValid(project.title)) return false;
    if (!isRequiredFieldValid(project.position)) return false;
    if (!project.duration && project.duration !== 0) return false;
    if (project.duration < PROJECT_VALIDATION.MIN_DURATION) return false;

    // Max length validations
    if (exceedsMaxLength(project.title, PROJECT_VALIDATION.TITLE_MAX_LENGTH)) return false;
    if (exceedsMaxLength(project.position, PROJECT_VALIDATION.POSITION_MAX_LENGTH)) return false;
    if (exceedsMaxLength(project.country, PROJECT_VALIDATION.COUNTRY_MAX_LENGTH)) return false;

    return true;
  }

  /**
   * Get validation error message for project
   */
  getProjectValidationMessage(project: any): string {
    const errors: string[] = [];

    // Check required fields
    if (!isRequiredFieldValid(project.title)) {
      errors.push('Title is required');
    }
    if (!isRequiredFieldValid(project.position)) {
      errors.push('Position is required');
    }
    if (!project.duration && project.duration !== 0) {
      errors.push('Duration is required');
    }
    if (project.duration < PROJECT_VALIDATION.MIN_DURATION) {
      errors.push('Duration must be 0 or greater');
    }

    // Check max lengths
    if (exceedsMaxLength(project.title, PROJECT_VALIDATION.TITLE_MAX_LENGTH)) {
      errors.push(`Title exceeds ${PROJECT_VALIDATION.TITLE_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(project.position, PROJECT_VALIDATION.POSITION_MAX_LENGTH)) {
      errors.push(`Position exceeds ${PROJECT_VALIDATION.POSITION_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(project.country, PROJECT_VALIDATION.COUNTRY_MAX_LENGTH)) {
      errors.push(`Country exceeds ${PROJECT_VALIDATION.COUNTRY_MAX_LENGTH} characters`);
    }

    return errors.join(', ');
  }

  /**
   * Validate high school education data
   */
  isHighSchoolValid(highSchool: HighSchoolEducationExtended): boolean {
    if (!isRequiredFieldValid(highSchool.highSchoolName)) return false;
    if (!isRequiredFieldValid(highSchool.fieldOfStudy)) return false;
    if (!highSchool.educationLevelID) return false;
    if (!highSchool.startDate) return false;
    if (!highSchool.endDate) return false;
    if (this.isEndBeforeStart(highSchool.startDate, highSchool.endDate)) return false;

    if (exceedsMaxLength(highSchool.highSchoolName, FIELD_VALIDATION.HIGH_SCHOOL_NAME_MAX_LENGTH)) return false;
    if (exceedsMaxLength(highSchool.fieldOfStudy, FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH)) return false;
    if (exceedsMaxLength(highSchool.city, FIELD_VALIDATION.CITY_MAX_LENGTH)) return false;
    if (exceedsMaxLength(highSchool.country, FIELD_VALIDATION.COUNTRY_MAX_LENGTH)) return false;

    return true;
  }

  /**
   * Validate university education data
   */
  isUniversityValid(university: UniversityEducationExtended): boolean {
    if (!isRequiredFieldValid(university.universityName)) return false;
    if (!isRequiredFieldValid(university.fieldOfStudy)) return false;
    if (!university.educationLevelID) return false;
    if (!university.startDate) return false;
    if (!university.endDate && !university.isCurrentlyStudying && !university.current) return false;
    if (!university.isCurrentlyStudying && !university.current && this.isEndBeforeStart(university.startDate, university.endDate)) return false;

    if (exceedsMaxLength(university.universityName, FIELD_VALIDATION.UNIVERSITY_NAME_MAX_LENGTH)) return false;
    if (exceedsMaxLength(university.fieldOfStudy, FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH)) return false;
    if (exceedsMaxLength(university.city, FIELD_VALIDATION.CITY_MAX_LENGTH)) return false;
    if (exceedsMaxLength(university.country, FIELD_VALIDATION.COUNTRY_MAX_LENGTH)) return false;

    return true;
  }

  /**
   * Validate language data
   */
  isLanguageValid(language: LanguageExtended): boolean {
    if (!isRequiredFieldValid(language.language)) return false;
    if (!isRequiredFieldValid(language.level)) return false;

    if (exceedsMaxLength(language.language, FIELD_VALIDATION.LANGUAGE_NAME_MAX_LENGTH)) return false;
    if (!ALLOWED_VALUES.LANGUAGE_LEVELS.includes(language.level as any)) return false;

    return true;
  }

  /**
   * Validate skill data
   */
  isSkillValid(skill: SkillExtended): boolean {
    if (!isRequiredFieldValid(skill.skillName)) return false;
    if (!isRequiredFieldValid(skill.level)) return false;

    if (exceedsMaxLength(skill.skillName, FIELD_VALIDATION.SKILL_NAME_MAX_LENGTH)) return false;

    return true;
  }

  /**
   * Get validation error message for language
   */
  getLanguageValidationMessage(language: LanguageExtended): string {
    const errors: string[] = [];

    if (!isRequiredFieldValid(language.language)) {
      errors.push('Language is required');
    }
    if (!isRequiredFieldValid(language.level)) {
      errors.push('Level is required');
    }
    if (exceedsMaxLength(language.language, FIELD_VALIDATION.LANGUAGE_NAME_MAX_LENGTH)) {
      errors.push(`Language name exceeds ${FIELD_VALIDATION.LANGUAGE_NAME_MAX_LENGTH} characters`);
    }
    if (!ALLOWED_VALUES.LANGUAGE_LEVELS.includes(language.level as any)) {
      errors.push('Invalid language level');
    }

    return errors.join(', ');
  }

  /**
   * Get validation error message for skill
   */
  getSkillValidationMessage(skill: SkillExtended): string {
    const errors: string[] = [];

    if (!isRequiredFieldValid(skill.skillName)) {
      errors.push('Skill name is required');
    }
    if (!isRequiredFieldValid(skill.level)) {
      errors.push('Level is required');
    }
    if (exceedsMaxLength(skill.skillName, FIELD_VALIDATION.SKILL_NAME_MAX_LENGTH)) {
      errors.push(`Skill name exceeds ${FIELD_VALIDATION.SKILL_NAME_MAX_LENGTH} characters`);
    }

    return errors.join(', ');
  }

  /**
   * Get validation error message for high school education
   */
  getHighSchoolValidationMessage(highSchool: HighSchoolEducationExtended): string {
    const errors: string[] = [];

    if (!isRequiredFieldValid(highSchool.highSchoolName)) {
      errors.push('School name is required');
    }
    if (!isRequiredFieldValid(highSchool.fieldOfStudy)) {
      errors.push('Field of study is required');
    }
    if (!highSchool.educationLevelID) {
      errors.push('Education level is required');
    }
    if (!highSchool.startDate) {
      errors.push('Start date is required');
    }
    if (!highSchool.endDate) {
      errors.push('End date is required');
    }
    if (this.isEndBeforeStart(highSchool.startDate, highSchool.endDate)) {
      errors.push('End date must be after start date');
    }
    if (exceedsMaxLength(highSchool.highSchoolName, FIELD_VALIDATION.HIGH_SCHOOL_NAME_MAX_LENGTH)) {
      errors.push(`School name exceeds ${FIELD_VALIDATION.HIGH_SCHOOL_NAME_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(highSchool.fieldOfStudy, FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH)) {
      errors.push(`Field of study exceeds ${FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(highSchool.city, FIELD_VALIDATION.CITY_MAX_LENGTH)) {
      errors.push(`City exceeds ${FIELD_VALIDATION.CITY_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(highSchool.country, FIELD_VALIDATION.COUNTRY_MAX_LENGTH)) {
      errors.push(`Country exceeds ${FIELD_VALIDATION.COUNTRY_MAX_LENGTH} characters`);
    }

    return errors.join(', ');
  }

  /**
   * Get validation error message for university education
   */
  getUniversityValidationMessage(university: UniversityEducationExtended): string {
    const errors: string[] = [];

    if (!isRequiredFieldValid(university.universityName)) {
      errors.push('University name is required');
    }
    if (!isRequiredFieldValid(university.fieldOfStudy)) {
      errors.push('Field of study is required');
    }
    if (!university.educationLevelID) {
      errors.push('Education level is required');
    }
    if (!university.startDate) {
      errors.push('Start date is required');
    }
    if (!university.endDate && !university.isCurrentlyStudying && !university.current) {
      errors.push('End date is required');
    }
    if (!university.isCurrentlyStudying && !university.current && this.isEndBeforeStart(university.startDate, university.endDate)) {
      errors.push('End date must be after start date');
    }
    if (exceedsMaxLength(university.universityName, FIELD_VALIDATION.UNIVERSITY_NAME_MAX_LENGTH)) {
      errors.push(`University name exceeds ${FIELD_VALIDATION.UNIVERSITY_NAME_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(university.fieldOfStudy, FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH)) {
      errors.push(`Field of study exceeds ${FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(university.city, FIELD_VALIDATION.CITY_MAX_LENGTH)) {
      errors.push(`City exceeds ${FIELD_VALIDATION.CITY_MAX_LENGTH} characters`);
    }
    if (exceedsMaxLength(university.country, FIELD_VALIDATION.COUNTRY_MAX_LENGTH)) {
      errors.push(`Country exceeds ${FIELD_VALIDATION.COUNTRY_MAX_LENGTH} characters`);
    }

    return errors.join(', ');
  }

  private isEndBeforeStart(startDate?: string | Date | null, endDate?: string | Date | null): boolean {
    if (!startDate || !endDate) return false;

    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    if (Number.isNaN(start) || Number.isNaN(end)) return false;

    return end < start;
  }
}
